import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Shield, Loader2 } from 'lucide-react';
import { useGetTopHellocians } from '../../services/user.service';
import { useGetMe } from '../../services/auth/auth';
import { createDirectChat } from '../../services/chat.service';
import { note2 } from '../../assets/assets';
import CustomAlert from '../CustomAlert';

const HellociansCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  const { data: topHellociansData, isLoading } = useGetTopHellocians();
  const { data: currentUser } = useGetMe();
  const hellocians = topHellociansData?.hellocians || [];

  const itemsPerPage = 2;
  const totalPages = Math.ceil(hellocians.length / itemsPerPage);

  const handleMessage = async (hellocian) => {
    if (!currentUser) {
      setAlert({
        show: true,
        message: 'Please log in to message our talents.',
        type: 'error'
      });
      return;
    }

    try {
      setIsCreatingChat(hellocian._id);
      const data = await createDirectChat(hellocian._id);
      const chat = data.chat;
      
      const basePath = currentUser.role === 'hellocian' ? '/hellocians/messages' : '/clients/messages';
      window.location.href = `${basePath}/${chat._id || chat.id}`;
    } catch (error) {
      console.error('Chat creation error:', error);
      setAlert({
        show: true,
        message: error.response?.data?.error || 'Failed to start chat. Please try again.',
        type: 'error'
      });
    } finally {
      setIsCreatingChat(null);
    }
  };

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % totalPages);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const getCurrentItems = () => {
    const startIndex = currentIndex * itemsPerPage;
    return hellocians.slice(startIndex, startIndex + itemsPerPage);
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto p-6 flex items-center justify-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-[#546FFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (hellocians.length === 0) {
    return null; // Or show a placeholder
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      {alert.show && (
        <CustomAlert 
          type={alert.type} 
          message={alert.message} 
          onClose={() => setAlert({ ...alert, show: false })} 
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium md:text-xl md:font-semibold text-gray-900 dark:text-white tracking-tight">
          Top rated Hellocians
        </h2>
        
        {/* Navigation Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={prevSlide}
            disabled={isAnimating}
            className="p-2  transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={nextSlide}
            disabled={isAnimating}
            className="p-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Carousel Container */}
      <div className="relative overflow-hidden">
        <div 
          className="flex transition-transform duration-300 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {Array.from({ length: totalPages }).map((_, pageIndex) => (
            <div key={pageIndex} className="flex gap-6 min-w-full">
              {hellocians
                .slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage)
                .map((hellocian) => (
                  <div
                    key={hellocian._id}
                    className="flex-1 transition-all duration-200 bg-[#FFFFFF] dark:bg-[#1d2026] rounded-[10px]"
                  >
                    {/* Profile Section */}
                    <div className="flex items-start justify-between p-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <img
                            src={hellocian.avatar}
                            alt={hellocian.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white dark:border-gray-800 shadow-sm"
                          />
                          {hellocian.verified && (
                            <div className="hidden absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800">
                              <Shield className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h3 className="md:font-medium font-normal text-[13px] md:text-base text-gray-900 dark:text-white leading-tight text-nowrap">
                            {hellocian.name}
                          </h3>
                          <p className="text-[10px] md:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {hellocian.role}
                          </p>
                        </div>
                      </div>
                      
                      {/* Message Button */}
                      <button 
                        onClick={() => handleMessage(hellocian)}
                        disabled={isCreatingChat === hellocian._id}
                        className="px-2 py-1 text-[#546FFF] dark:text-blue-400 text-xs md:text-base font-medium flex items-center gap-2 hover:bg-blue-50 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                      >
                        {isCreatingChat === hellocian._id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          'Message'
                        )}
                      </button>
                    </div>

                    {/* Stats Section */}
                    <div className="flex items-center justify-between pb-4 px-4 pt-3">
                      {/* Tasks */}
                      <div className="flex items-center gap-2">
                       <img src={note2} alt='tasks'/>
                        <span className="text-xs md:text-sm md:font-medium font-normal text-[#141522] dark:text-gray-200">
                          {hellocian.tasks} Task
                        </span>
                      </div>

                      {/* Rating */}
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                            <Star className="md:w-4 w-3 md:h-4 h-3 fill-[#FFB054] text-[#FFB054]" />
                          <span className="text-xs md:text-sm md:font-medium font-normal text-[#141522] dark:text-gray-200">
                            {hellocian.rating}
                          </span>
                        </div>
                        <span className="text-xs md:text-sm md:font-medium font-normal text-[#141522] dark:text-gray-400">
                          ({hellocian.reviews} Reviews)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      {/* Page Indicators */}
      <div className="flex items-center justify-center gap-2 mt-6 hidden">
        {Array.from({ length: totalPages }).map((_, index) => (
          <button
            key={index}
            onClick={() => {
              if (!isAnimating) {
                setIsAnimating(true);
                setCurrentIndex(index);
                setTimeout(() => setIsAnimating(false), 300);
              }
            }}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              index === currentIndex
                ? 'bg-blue-500 w-6'
                : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default HellociansCarousel;